import { useState, useEffect, useContext } from "react";
import "./LoginPage.css";
import { TbLogin2 } from "react-icons/tb";
import LoginLogo from "../../assets/images/login-logo.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { GeneralContext } from "../../GeneralProvider";
import { useToaster } from "../../ToasterProvider";
import { useLanguage } from "../../LanguageProvider";
import Services from "../../services/Services";
import OTPInput from "../../components/OTPInput/OTPInput";

export default function ForgotPassword() {
  const { toastError, toastSuccess, toastWarning } = useToaster();
  const { getTranslation } = useLanguage();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [enteredOTP, setEnteredOTP] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [buttonText, setButtonText] = useState("Send Code");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const minLengthOK = password?.length >= 8;
  const hasUppercase = /\p{Lu}/u.test(password);
  const hasLowercase = /\p{Ll}/u.test(password);
  const hasNumber = /\p{N}/u.test(password);
  const hasSpecialChar = /[^\p{L}\p{N}\s]/u.test(password);

  const getToken = () => {
    Services.GetToken()
      .then((res) => {
        if (res.data.status) {
          setToken(res.data.result.token);
        }
      })
      .catch((err) => console.log(err));
  };

  const generateSixDigitCode = () =>
    Math.floor(100000 + Math.random() * 900000);

  const handleSendOTP = () => {
    setIsLoading(true);
    let otp = generateSixDigitCode();
    setOtpCode(otp);

    Services.SendOTPForResetPassword(token, otp, email)
      .then((res) => {
        setIsLoading(false);
        if (res.data.status) {
          setCurrentStep(1);
          setButtonText("Continue");
          setResendTimer(60);
        } else {
          toastError("Please check your email address.");
        }
      })
      .catch((err) => console.log(err));
  };

  const resetPassword = () => {
    setIsLoading(true);
    Services.ResetPassword(token, enteredOTP, email, password)
      .then((res) => {
        setIsLoading(false);
        if (res.data.status) {
          toastSuccess("You have succesfully changed your password.");
        } else {
          toastError("Something went wrong please try again later.");
        }
        navigate("/");
      })
      .catch((err) => console.log(err));
  };

  const checkOTP = () => {
    if (otpCode.toString() === enteredOTP.toString()) {
      setCurrentStep(2);
      setButtonText("Change Password");
    } else {
      toastError("Your OTP is wrong. Please check again.");
    }
  };

  const onChangeOTP = (otp) => {
    setEnteredOTP(otp.join(""));
  };

  const buttonOnClick = () => {
    if (currentStep === 0) {
      handleSendOTP();
    } else if (currentStep === 1) {
      checkOTP();
    } else {
      resetPassword();
    }
  };

  useEffect(() => {
    getToken();
  }, []);

  useEffect(() => {
    let interval;
    if (currentStep === 1 && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentStep, resendTimer]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleRepeatPasswordVisibility = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  return (
    <div className="container">
      <div className="left-circle"></div>
      <div className="right-circle"></div>
      <button onClick={() => navigate("/")} className="redirect-button">
        Log In <TbLogin2 size={25} />
      </button>

      <div className="left-section">
        <div className="left-section-inner-container">
          <span className="left-section-text">
            Manage Your Workforce in{" "}
            <span className="left-section-inner-text">One Space</span>
          </span>
          <span className="left-section-subtitle">
            Sign in now and start the journey
          </span>
        </div>
      </div>
      <div className="right-section">
        <div className="right-section-inner-container">
          <img src={LoginLogo} height={50} />
          {/* <span className="right-section-text">
            Welcome to <span className="right-section-inner-text">Lodos</span>
          </span> */}

          <div className="reset-password-title">
            {getTranslation("resetYourPassword") || "Reset Your Password"}
          </div>

          {currentStep === 0 && (
            <div className="auth-form">
              <label className="input-label">Email</label>
              <input
                className="input-default"
                type="text"
                id="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <span className="terms-text">
                Please enter your account's email address.
              </span>
            </div>
          )}

          {currentStep === 1 && (
            <>
              <OTPInput onChangeOTP={onChangeOTP} />
              <span className="terms-text">
                Please enter the OTP code sent to your email.
              </span>
              <div className="resend-row">
                <button
                  type="button"
                  className="resend-button"
                  disabled={resendTimer > 0 || isLoading}
                  onClick={handleSendOTP}
                >
                  {getTranslation("resend")}
                </button>
                {resendTimer > 0 && (
                  <span className="resend-timer">{resendTimer}s</span>
                )}
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <label className="input-label" style={{ width: "500px" }}>
                Password*
              </label>
              <div className="input-container" style={{ width: "500px" }}>
                <input
                  className="input-default"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value.replace(/\s/g, ""))
                  }
                  onKeyDown={(e) => {
                    if (e.key === " ") {
                      e.preventDefault();
                      toastWarning(
                        getTranslation("passwordCannotContainSpaces")
                      );
                    }
                  }}
                />
                <span
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              <label className="input-label" style={{ width: "500px" }}>
                Repeat Password*
              </label>
              <div className="input-container" style={{ width: "500px" }}>
                <input
                  className="input-default"
                  type={showRepeatPassword ? "text" : "password"}
                  id="repeatPassword"
                  placeholder="Repeat Password"
                  value={repeatPassword}
                  onChange={(e) =>
                    setRepeatPassword(e.target.value.replace(/\s/g, ""))
                  }
                  onKeyDown={(e) => {
                    if (e.key === " ") {
                      e.preventDefault();
                      toastWarning(
                        getTranslation("passwordCannotContainSpaces")
                      );
                    }
                  }}
                />
                <span
                  className="password-toggle"
                  onClick={toggleRepeatPasswordVisibility}
                >
                  {showRepeatPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              <div
                style={{
                  textAlign: "left",
                  fontSize: "14px",
                  flexDirection: "row",
                  display: "flex",
                  gap: "15px",
                  marginTop: "15px",
                }}
              >
                <div>
                  <p
                    style={{
                      color: minLengthOK ? "#60b85a" : "#4d5885",
                      margin: "4px 0",
                    }}
                  >
                    • At least 8 characters
                  </p>
                  <p
                    style={{
                      color: hasSpecialChar ? "#60b85a" : "#4d5885",
                      margin: "4px 0",
                    }}
                  >
                    • At least 1 special character
                  </p>
                  <p
                    style={{
                      color: hasNumber ? "#60b85a" : "#4d5885",
                      margin: "4px 0",
                    }}
                  >
                    • At least 1 number
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      color: hasUppercase ? "#60b85a" : "#4d5885",
                      margin: "4px 0",
                    }}
                  >
                    • At least 1 uppercase letter
                  </p>
                  <p
                    style={{
                      color: hasLowercase ? "#60b85a" : "#4d5885",
                      margin: "4px 0",
                    }}
                  >
                    • At least 1 lowercase letter
                  </p>
                </div>
              </div>
            </>
          )}

          <button
            type="button"
            className="login-button"
            onClick={buttonOnClick}
            disabled={
              currentStep === 2 &&
              (!minLengthOK ||
                !hasUppercase ||
                !hasLowercase ||
                !hasNumber ||
                !hasSpecialChar ||
                password !== repeatPassword)
            }
          >
            {isLoading ? <div className="spinner"></div> : buttonText}
          </button>
          <span className="already-text">
            Don't have an account?{" "}
            <span
              onClick={() => navigate("/register")}
              className="second-sign-up"
            >
              Sign up
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
